import { alpha, type Theme } from "@mui/material/styles";

/** Width of the parent's detail-panel toggle column; the panel reserves the same leading space. */
export const SERIES_TOGGLE_WIDTH = 50;

/** MUI's default horizontal cell padding. */
export const CELL_PADDING = 10;
/** Height of a single-line child row, which the loading skeleton row matches. */
const CHILD_ROW_HEIGHT = 38;

const seriesTint = (theme: Theme) => alpha(theme.palette.primary.main, 0.07);
const seriesHoverTint = (theme: Theme) => alpha(theme.palette.primary.main, 0.11);
const seriesBar = (theme: Theme, color = theme.palette.primary.main) => `inset 3px 0 0 ${color}`;

/**
 * Parent-grid styles that join an expanded series row and its detail panel into
 * one block with a shared tint and accent bar.
 */
export const getSeriesGridSx = (theme: Theme) => ({
  "& .MuiDataGrid-row--detailPanelExpanded": {
    backgroundColor: seriesTint(theme),
    boxShadow: seriesBar(theme),
    "&:hover": {
      backgroundColor: seriesHoverTint(theme),
      boxShadow: seriesBar(theme, theme.palette.primary.dark),
    },
  },
});

/** Child occurrence rows: no dividers between them. */
export const getSeriesChildGridSx = (theme: Theme) => ({
  // No outer border so columns sit exactly on the parent grid's columns.
  border: 0,
  backgroundColor: "transparent",
  "& .MuiDataGrid-columnHeaders": { display: "none" },
  "& .MuiDataGrid-cell": { backgroundColor: "transparent" },
  // No dividers between children; hovering a row darkens its section of the accent bar.
  // Doubled `&` outranks the parent grid's own row rule, which also matches these nested rows.
  "&& .MuiDataGrid-row": {
    "--rowBorderColor": "transparent",
    minHeight: CHILD_ROW_HEIGHT,
    backgroundColor: "transparent",
  },
  "&& .MuiDataGrid-row:hover": {
    backgroundColor: theme.palette.action.hover,
    boxShadow: seriesBar(theme, theme.palette.primary.dark),
  },
});

/** "Show more" sits under the child deadlines, inside the series block. */
export const getSeriesShowMoreSx = (theme: Theme) => ({
  cursor: "pointer",
  py: 1,
  pl: `${SERIES_TOGGLE_WIDTH + CELL_PADDING}px`,
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
    boxShadow: seriesBar(theme, theme.palette.primary.dark),
  },
});

/** A placeholder child row shown while occurrences load. */
export const seriesSkeletonRowSx = {
  display: "flex",
  alignItems: "center",
  height: CHILD_ROW_HEIGHT,
};

/**
 * The panel continues the expanded parent row's block: same tint, and an accent
 * bar running unbroken past the rows, skeleton, and bottom padding.
 */
export const getSeriesPanelSx = (theme: Theme) => ({
  pb: 0.5,
  backgroundColor: seriesTint(theme),
  boxShadow: seriesBar(theme),
});
